// The Gallery: real photographs of space, each with its credit and licence, in pages of sections (src/ui/GalleryPanel.tsx).
// The pictures and their words come from data-src/gallery-sources/gallery.json through scripts/build-gallery.py, which
// writes public/data/gallery.json (fetched when the Gallery opens, and kept for offline use with the app) and the
// pictures to public/gallery/ (fetched when shown, kept once seen).
import { signal } from '@preact/signals';

/** Creative Commons licences carry their version; 'Used with permission (granted <date>)' is a holder's written
 * permission, shown as "Used with permission"; 'Credit only' is a copyrighted picture whose holder allows education,
 * shown with its credit and no licence label. */
export type Licence =
  | 'CC BY 4.0' | 'CC BY 3.0' | 'CC BY 2.0' | 'CC BY' | 'CC BY-SA 3.0 IGO' | 'CC BY-SA 4.0'
  | 'Public domain (NASA)' | 'Public domain' | 'ESA (with permission)' | 'Credit only' | `Used with permission${string}`;

export interface GalleryImage {
  id: string;
  title: string;
  /** Two or three sentences: what it is, when and with what it was taken, and what it showed. */
  caption: string;
  telescope: string;
  /** When it was taken or released (YYYY, YYYY-MM or YYYY-MM-DD). */
  date: string;
  year: number;
  /** Exactly as the source gives it. */
  credit: string;
  licence: Licence;
  /** The page it comes from. */
  source: string;
  /** What to select for "Find it in the sky", as share links name it (dso:M16, body:Jupiter, small:Ceres). */
  sel?: string;
  w: number;
  h: number;
  /** The picture's dominant colour, shown until it loads. */
  colour: string;
}

export interface GallerySection {
  id: string;
  title?: string;
  intro?: string;
  /** A timeline lists its pictures by year. */
  layout?: 'grid' | 'timeline';
  items: string[];
}

/** A moon's or a dwarf planet's facts, shown at the top of its page (one set for each body the page shows). */
export interface GalleryFact {
  /** The body's name, where a page shows more than one. */
  name?: string;
  /** Left out for a candidate dwarf planet, whose size is too uncertain to give. */
  diameterKm?: number;
  /** Its diameter as a share of Earth's, in per cent. */
  earthPercent?: number;
  /** An elongated body's length, width and height (Haumea). */
  dimensionsKm?: number[];
  /** The year it was discovered, and by whom. */
  discovered?: string;
  discoverer?: string;
  /** Its average distance from its planet (from the Sun, for a dwarf planet). */
  distanceKm?: number;
  /** Where the facts come from (JPL), and what each page vouches for (diameter, discovery, distance) where known. */
  sources: { label: string; href: string; about?: string }[];
}

export interface GalleryPage {
  id: string;
  /** Newest at the top of the Gallery, then our solar system, then the deep sky. */
  group: 'newest' | 'solar' | 'deep';
  /** On the home, a solar-system page is one of the planets (outwards from the Sun), their moons (by planet), the dwarf
   * planets, or the Sun and the smaller worlds. */
  part?: 'planets' | 'moons' | 'dwarfs' | 'worlds';
  title: string;
  intro: string;
  /** The Sun, the Moon or a planet: its card in the sky links here. */
  body?: string;
  /** A moon's planet (its page). */
  parent?: string;
  facts?: GalleryFact[];
  /** The body "Compare with Earth" shows (src/ui/gallery/sizes-link.ts). */
  sizes?: string;
  /** Pages it links to besides its moons: where a part of it moved (Ceres, from the asteroids, to its own page). */
  see?: string[];
  /** Why a page has no pictures (Haumea: none shows it as more than a point of light). */
  note?: string;
  cover?: string;
  sections: GallerySection[];
}

export interface GalleryHighlight {
  page: string;
  section?: string;
  title: string;
  text: string;
  cover?: string;
}

export interface Gallery {
  pages: GalleryPage[];
  highlights: GalleryHighlight[];
  images: GalleryImage[];
  byId: Map<string, GalleryImage>;
  pageById: Map<string, GalleryPage>;
  /** Pictures of each object in the sky, by its selection id. */
  bySel: Map<string, GalleryImage[]>;
  /** The page of each body (Moon, Mars…). */
  pageOfBody: Map<string, GalleryPage>;
  /** The moons' pages of each planet's page, in order. */
  moonsOf: Map<string, GalleryPage[]>;
}

/** The Gallery once loaded (null until then). */
export const gallery = signal<Gallery | null>(null);

/** Index the data: every section keeps only pictures that exist, and empty sections go, as do pages left with nothing
 * (a page with a note, such as Haumea's, stays without pictures). */
export function indexGallery(data: { pages: GalleryPage[]; highlights?: GalleryHighlight[]; images: GalleryImage[] }): Gallery {
  const byId = new Map(data.images.map((i) => [i.id, i]));
  const pages = data.pages
    .map((p) => ({ ...p, sections: p.sections.map((s) => ({ ...s, items: s.items.filter((id) => byId.has(id)) })).filter((s) => s.items.length) }))
    .filter((p) => p.sections.length || p.note);
  const pageById = new Map(pages.map((p) => [p.id, p]));
  const bySel = new Map<string, GalleryImage[]>();
  for (const i of data.images) if (i.sel) bySel.set(i.sel, [...(bySel.get(i.sel) ?? []), i]);
  const pageOfBody = new Map(pages.filter((p) => p.body).map((p) => [p.body!, p]));
  const highlights = (data.highlights ?? []).filter((h) => pageById.has(h.page));
  const moonsOf = new Map<string, GalleryPage[]>();
  for (const p of pages) if (p.parent && pageById.has(p.parent)) moonsOf.set(p.parent, [...(moonsOf.get(p.parent) ?? []), p]);
  return { pages, highlights, images: data.images, byId, pageById, bySel, pageOfBody, moonsOf };
}

let loading: Promise<Gallery> | null = null;
/** Load the Gallery's words (a separate file, so the app starts without it). A failed load can simply be tried again. */
export function loadGallery(): Promise<Gallery> {
  loading ??= fetch(`${import.meta.env.BASE_URL}data/gallery.json`)
    .then((r) => {
      if (!r.ok) throw new Error(`gallery ${r.status}`);
      return r.json() as Promise<Parameters<typeof indexGallery>[0]>;
    })
    .then((d) => (gallery.value = indexGallery(d)));
  return loading.catch((e) => {
    loading = null;
    throw e;
  });
}

export const imageUrl = (id: string, size: 480 | 2048) => `${import.meta.env.BASE_URL}gallery/${id}-${size}.webp`;

/** Every picture on a page once, in page order. */
export function pageImages(g: Gallery, p: GalleryPage): GalleryImage[] {
  const ids = [...new Set(p.sections.flatMap((s) => s.items))];
  return ids.map((id) => g.byId.get(id)!).filter(Boolean);
}

/** The picture that stands for a page: its chosen cover, else the newest of its timeline (the clearest view), else its
 * first; none for a page without pictures. */
export function coverOf(g: Gallery, p: GalleryPage): GalleryImage | undefined {
  const timeline = p.sections.find((s) => s.layout === 'timeline');
  return g.byId.get(p.cover ?? '') ?? (timeline && g.byId.get(timeline.items[timeline.items.length - 1])) ?? pageImages(g, p)[0];
}

/** How the licence is named beside the credit, and where it links; nothing for a picture shown with its credit alone. */
export function licenceText(l: Licence): { text: string; href?: string } | null {
  if (l.startsWith('Used with permission')) return { text: 'Used with permission' };
  switch (l) {
    case 'Credit only': return null;
    case 'CC BY 4.0': return { text: l, href: 'https://creativecommons.org/licenses/by/4.0/' };
    case 'CC BY 3.0': return { text: l, href: 'https://creativecommons.org/licenses/by/3.0/' };
    case 'CC BY 2.0': return { text: l, href: 'https://creativecommons.org/licenses/by/2.0/' };
    // A processor who names no version: Creative Commons' page for the licence (its latest version).
    case 'CC BY': return { text: l, href: 'https://creativecommons.org/licenses/by/' };
    case 'CC BY-SA 3.0 IGO': return { text: l, href: 'https://creativecommons.org/licenses/by-sa/3.0/igo/' };
    case 'CC BY-SA 4.0': return { text: l, href: 'https://creativecommons.org/licenses/by-sa/4.0/' };
    case 'ESA (with permission)': return { text: 'Used with ESA’s permission' };
    default: return { text: 'Public domain' };
  }
}
